import { Suspense, useEffect, type ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SVGSprite } from './App.styled';
import useAuth from '@contexts/useAuth';
import AuthenticatedRoutes from './routes/AuthenticatedRoutes';
import UnauthenticatedRoutes from './routes/UnauthenticatedRoutes';
import PaymentWallRoutes from './routes/PaymentWallRoutes';
import OnboardingRoutes from './routes/OnboardingRoutes';
import BackofficeRoutes from './routes/BackofficeRoutes';
import EulaAcceptance from './pages/EulaAcceptance/EulaAcceptance';
import MfaSetupWall from './pages/MfaSetupWall/MfaSetupWall';
import { LoadingSpinner } from '@components/LoadingSpinner';
import SnackbarPortal from '@components/2-molecules/SnackbarPortal';
import { DROP_DOWN_MENU_CONTAINER_ID } from '@components/2-molecules/DropDownMenuPortal/DropDownMenuPortal';
import { useFeatureGate, FeatureGate } from '@featureFlags/index';
import { isSuperadminAccount } from '@utils/superadminAccounts';
import { consumeReturnTo, rememberReturnTo } from '@utils/ssoSession';
import { isSharedAppPath } from './pages/Apps/appRunPath';
import type { User } from '@contexts/AuthContextStore';

type Screen = 'unauth' | 'backoffice' | 'paywall' | 'eula' | 'mfa' | 'onboarding' | 'app';

// Only the 'app' screen mounts BaseLayout, which carries the global navbar.
// 'backoffice' bypasses all walls; 'unauth' is the logged-out auth pages.
function resolveScreen(user: User | null, pathname: string, isOnboardingFormEnabled: boolean): Screen {
  if (!user) return 'unauth';
  if (
    pathname.startsWith('/backoffice-admin') &&
    user.orgRole === 'AIBUILD_ADMIN' &&
    !isSuperadminAccount(user.email)
  ) {
    return 'backoffice';
  }
  // Let the invite flow bypass walls so the user can accept/decline first.
  if (user.pendingInviteToken && pathname.startsWith('/org/invite')) return 'paywall';
  if (!user.eulaAccepted) return 'eula';
  if (user.mfaEnforced && !user.mfaEnabled) return 'mfa';
  if (user.billingStatus !== 'ACTIVE') return 'paywall';
  if (isOnboardingFormEnabled && !user.hasCompletedOnboarding) return 'onboarding';
  return 'app';
}

function App() {
  const { user, isInitializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Lead-qualification onboarding (AIB-6641) is rolled out behind a Statsig
  // gate. When the gate is OFF, paid users skip straight into the main app
  // exactly like they did before AIB-6641 shipped — no onboarding UI, no
  // forced redirect. The gate is fail-closed (see `useFeatureGate`), so any
  // SDK init failure or missing client key also disables the form.
  const isOnboardingFormEnabled = useFeatureGate(FeatureGate.OS_ONBOARDING_FORM);

  // When the user logs in with a pending invite token, redirect to the invite page
  useEffect(() => {
    if (
      user?.pendingInviteToken &&
      !location.pathname.startsWith('/org/invite')
    ) {
      navigate(`/org/invite/token/${encodeURIComponent(user.pendingInviteToken)}`, {
        replace: true,
      });
    }
  }, [user?.pendingInviteToken, navigate, location.pathname]);

  const screen = resolveScreen(user, location.pathname, isOnboardingFormEnabled);

  // The paywall and onboarding walls redirect anywhere they do not own, so a
  // shared app opened behind one is remembered and reopened once past it.
  useEffect(() => {
    if (user && screen !== 'app' && isSharedAppPath(location.pathname)) rememberReturnTo(location.pathname);
  }, [user, screen, location.pathname]);

  useEffect(() => {
    if (isInitializing || screen !== 'app') return;
    const returnTo = consumeReturnTo();
    if (returnTo) globalThis.location.assign(returnTo);
  }, [isInitializing, screen]);

  // Don't render anything until we've checked localStorage
  if (isInitializing) {
    return (
      <>
        <SVGSprite />
        <LoadingSpinner text="Initializing..." />
      </>
    );
  }

  const screens: Record<Screen, ReactElement> = {
    unauth: <UnauthenticatedRoutes />,
    backoffice: <BackofficeRoutes />,
    paywall: <PaymentWallRoutes />,
    eula: <EulaAcceptance />,
    mfa: <MfaSetupWall />,
    onboarding: <OnboardingRoutes />,
    app: <AuthenticatedRoutes />,
  };

  return (
    <>
      <SVGSprite />
      <Suspense fallback={<LoadingSpinner />}>{screens[screen]}</Suspense>
      {/* Portal target and renderer for snackbar notifications */}
      <div id="snackbar-container" />
      <SnackbarPortal />
      {/* Portal target for dropdown menus */}
      <div id={DROP_DOWN_MENU_CONTAINER_ID} />
    </>
  );
}

export default App;
